<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { CheckIcon, ChevronUpDownIcon, BuildingOfficeIcon, MinusIcon, PlusIcon } from '@heroicons/vue/20/solid'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue'
import adminAPI from '@/services/api'
import { useClaimStore, useNotificationStore } from '@/store';
import { TravelAgencyCompany, RetrieveClaimWithRelationsResponse } from '@upclaim/sdk/src/data-contracts';
import { storeToRefs } from 'pinia';
import AlertModal from '../ui/modals/AlertModal.vue';
import { computePermissionForCurrentUser } from '@/utils/directives/v-permission';

const props = defineProps<{
  claimId: string;
  currentTravelAgencyCompany: TravelAgencyCompany
}>();

onMounted(() => {
  watch(() => props.claimId, (claimId) => {
    if (claimId) {
      fetchBusinessTravelCompanies(claimId);
    }
  }, { immediate: true });

  watch(() => props.currentTravelAgencyCompany, (travelAgencyCompany) => {
    // @ts-ignore
    selectedEnterprise.value = travelAgencyCompany || defaultValue;
  }, { immediate: true });
  watch(selectedEnterprise, (newEnterprise) => selectEnterprise(newEnterprise));
});

async function fetchBusinessTravelCompanies(claimId: string) {
  const response = await adminAPI.listBusinessTravelCompaniesForClaim(claimId);
  if (response) {
    enterprises.value = [
      { displayName: 'Aucune', domain: "", iconUrl: null, id: null },
      ...response.travelAgencyCompanies as any
    ];
  }
}


const query = ref('')
const defaultValue = { displayName: 'Aucune', domain: "", iconUrl: null, id: null };
const enterprises = ref([ defaultValue ]);
const selectedEnterprise = ref<any>()
const filteredEnterprises = computed(() =>
  query.value === ''
    ? enterprises.value
    : enterprises.value.filter((enterprise) => {
        return enterprise.displayName.toLowerCase().includes(query.value.toLowerCase())
      })
);

async function selectEnterprise(newEnterprise, inputChange = false) {
  if (inputChange && newEnterprise) {
    const payload = newEnterprise?.id
      ? { travelAgencyCompanyId: newEnterprise.id }
      : { unset: true };
    // @ts-ignore
    const { success } = await adminAPI.setTravelAgencyCompanyForClaim(props.claimId, payload);
    if (success) {
      useNotificationStore().emitNotification({ title: "Entreprise enregistrée" });
      fetchBusinessTravelCompanies(props.claimId);
    }
  }
}

const createEnterpriseOpen = ref(false);
const createEnterpriseForm = ref({
  displayName: "",
  domain: "",
  iconUrl: undefined,
  mainContactEmail: undefined,
});

async function createEnterprise() {
  const { success } = await adminAPI.createTravelAgencyCompanyFromClaim(props.claimId, {
    ...createEnterpriseForm.value
  });
  if (success) {
    createEnterpriseOpen.value = false;
    useNotificationStore().emitNotification({
      title: "Entreprise ajoutée avec succès"
    });
    fetchBusinessTravelCompanies(props.claimId);
  }
}
</script>
<template>
  <AlertModal
    type="info"
    title="Ajouter une entreprise"
    confirm-message="Créer"
    :open="createEnterpriseOpen"
    @close="createEnterpriseOpen = false"
    @cancel="createEnterpriseOpen = false"
    @confirm="() => createEnterprise()"
  >
   <div>
      <div class="flex justify-between">
        <label for="name" class="block text-sm font-medium leading-6 text-gray-900">Nom de l'entreprise</label>
        <span class="text-sm leading-6 text-gray-500">Requis</span>
      </div>
      <div class="mt-2">
        <input v-model="createEnterpriseForm.displayName" type="text" name="name" id="name" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" placeholder="RadioFrance" aria-describedby="enterprise-name-mandatory" />
      </div>
      <div class="flex justify-between mt-2">
        <label for="email" class="block text-sm font-medium leading-6 text-gray-900">Domaine</label>
        <span class="text-sm leading-6 text-gray-500" id="email-optional">Requis</span>
      </div>
      <div class="mt-2">
        <input v-model="createEnterpriseForm.domain" type="text" name="domain" id="domain" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" placeholder="radiofrance.fr" aria-describedby="domain-name-mandatory" />
      </div>
      <div class="flex justify-between mt-2">
        <label for="email" class="block text-sm font-medium leading-6 text-gray-900">Email du référent</label>
        <span class="text-sm leading-6 text-gray-500" id="email-optional">Optionnel</span>
      </div>
      <div class="mt-2">
        <input v-model="createEnterpriseForm.mainContactEmail" type="email" name="email" id="email" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" placeholder="francois@radiofrance.fr" aria-describedby="email-optional" />
      </div>
   </div> 
  </AlertModal>
  
  <!-- <pre>{{ selectedEnterprise }}</pre> -->
  <Combobox as="div" v-model="selectedEnterprise"  @update:model-value="(data) => selectEnterprise(data, true)" :disabled="!computePermissionForCurrentUser(['UPCLAIM_ADMIN', 'CUSTOMER_ADMIN', 'CUSTOMER_ACCOUNT_MANAGER'])">
    <!-- <ComboboxLabel class="block mb-2 text-sm font-medium leading-6 text-gray-900">Assigned to</ComboboxLabel> -->
    <div class="relative">
      <ComboboxInput class="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-12 text-gray-900 shadow ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
        @change="query = $event.target.value" :display-value="(enterprise: any) => enterprise?.displayName" />
      <ComboboxButton class="absolute inset-y-0 right-0 flex items-center px-2 rounded-r-md focus:outline-none">
        <ChevronUpDownIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
      </ComboboxButton>

      <ComboboxOptions v-if="filteredEnterprises.length > 0" class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
        <ComboboxOption v-for="(enterprise, index) in filteredEnterprises" :key="index" :value="enterprise" as="template" v-slot="{ active, selected }">
          <li :class="['relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-blue-600 text-white' : 'text-gray-900']">
            <div class="flex items-center">
              <!-- <img v-if="enterprise.iconUrl" :src="enterprise.iconUrl" alt="" :class="['flex-shrink-0 w-6 h-6 rounded-full']" /> -->
              <span v-if="true">
                <BuildingOfficeIcon v-if="enterprise.id" :class="['w-6 h-6 text-blue-700', active ? 'text-white' : 'text-blue-600']" />
                <MinusIcon v-else  :class="['w-6 h-6 text-blue-700', active ? 'text-white' : 'text-blue-600']" />
              </span>
              <span :class="['ml-3 truncate', selected && 'font-semibold']">
                {{ enterprise.displayName }}
              </span>
            </div>

            <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-blue-600']">
              <CheckIcon class="w-5 h-5" aria-hidden="true" />
            </span>
          </li>
        </ComboboxOption>
      </ComboboxOptions>
      <ComboboxOptions v-else class="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
        <ComboboxOption as="template" v-slot="{ active, selected }" @click="() => { createEnterpriseOpen = true; createEnterpriseForm.displayName = query }">
          <li :class="['relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-blue-600 text-white' : 'text-gray-900']">
            <div class="flex items-center cursor-pointer">
              <!-- <img v-if="enterprise.iconUrl" :src="enterprise.iconUrl" alt="" :class="['flex-shrink-0 w-6 h-6 rounded-full']" /> -->
              <span v-if="true">
                <PlusIcon :class="['w-6 h-6 text-blue-700', active ? 'text-white' : 'text-blue-600']" />
              </span>
              <span :class="['ml-3 truncate', selected && 'font-semibold']">
                Ajouter une entreprise
              </span>
            </div>
          </li>
        </ComboboxOption>
      </ComboboxOptions>
    </div>
  </Combobox>
</template>